/**
 * Top Contractors Section
 *
 * Displays top-rated contractors with:
 * - Section heading with loading indicator
 * - Horizontal scrollable contractor cards (data from API)
 * - Navigation arrow
 */

'use client';

import { useRef, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { ContractorCard, useTopContractors } from '@/src/entities/contractor';
import { IconButton } from '@/src/shared/ui/button/IconButton';
import { Typography } from '@/src/shared/ui/typography/Typography';

export function TopContractorsSection() {
  const t = useTranslations('landing.topContractors');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const {
    contractors,
    loading,
    error,
    isRefetching,
  } = useTopContractors({
    limit: 10,
    // useMock is auto-detected from NEXT_PUBLIC_IS_DEVELOPMENT env variable
  });

  // Debug logging
  if (typeof window !== 'undefined') {
    console.log('TopContractorsSection render:', {
      loading,
      isRefetching,
      contractorsCount: contractors.length,
      error,
    });
  }


  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      // Show left arrow if not at the start
      setShowLeftArrow(scrollLeft > 0);

      // Show right arrow if not at the end (with 1px tolerance)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Update arrow visibility when contractors load or change
  useEffect(() => {
    checkScrollPosition();
  }, [contractors]);

  // Update arrow visibility on mount and window resize
  useEffect(() => {
    checkScrollPosition();

    const handleResize = () => checkScrollPosition();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 800;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
      // Check position after scroll animation completes
      setTimeout(checkScrollPosition, 300);
    }
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 800;
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
      // Check position after scroll animation completes
      setTimeout(checkScrollPosition, 300);
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background SVG Pattern - positioned from bottom */}
      <div
        className="absolute bottom-0 left-0 w-full h-[300px]"
        style={{
          backgroundImage: 'url(/images/banners/background.svg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
        }}
      />

      <div
        className="absolute bottom-0 left-0 w-full h-[300px] opacity-40 bg-gradient-main"
      />

      <div className="relative container-auto">
        {/* Section Heading with inline loading/error indicators */}
        <div className="mb-10 flex items-center justify-between gap-4">
          <Typography variant="h1" weight="bold" className="text-[#2C2C2C]">
            {t('title')}
          </Typography>

          {/* Loading indicator */}
          {loading && (
            <span className="text-sm text-text-secondary animate-pulse">
              {t('loading')}
            </span>
          )}

          {/* Background refetch indicator */}
          {!loading && isRefetching && (
            <span className="text-sm text-text-secondary">
              {t('updating')}
            </span>
          )}

          {/* Error indicator */}
          {error && !loading && (
            <span className="text-sm text-red-600" title={error}>
              {t('errorLoading')}
            </span>
          )}
        </div>

        {/* Contractors with navigation button */}
        <div className="relative">
          {/* Scrollable container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide px-6"
            style={{ scrollPaddingLeft: '24px', scrollPaddingRight: '24px' }}
            onScroll={checkScrollPosition}
          >
            {/* Show skeleton loaders during initial load */}
            {loading && contractors.length === 0 && (
              <>
                {[1, 2, 3, 4].map((index) => (
                  <div
                    key={`skeleton-${index}`}
                    className="flex-none w-[285px] h-[380px] bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </>
            )}

            {/* Show actual contractors */}
            {contractors.map((contractor) => (
              <div key={contractor.id} className="flex-none w-[285px]">
                <ContractorCard
                  {...contractor}
                />
              </div>
            ))}

            {/* Empty state */}
            {!loading && contractors.length === 0 && (
              <div className="w-full text-center py-8">
                <Typography color="secondary">
                  {t('noContractors')}
                </Typography>
              </div>
            )}
          </div>

          {/* Navigation Arrows - show based on scroll position */}
          {/* Left Arrow - only show if not at the start */}
          {showLeftArrow && (
            <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 z-10">
              <IconButton
                iconName="arrow-left"
                onClick={handleScrollLeft}
                size="lg"
                aria-label="Scroll left"
              />
            </div>
          )}

          {/* Right Arrow - only show if not at the end */}
          {showRightArrow && (
            <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 z-10">
              <IconButton
                iconName="arrow-right"
                onClick={handleScrollRight}
                size="lg"
                aria-label="Scroll right"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
